/**
 * Camada 2 da ficha de operador — os sinais VIVOS (presença, carga, padrão
 * comportamental), lidos do estado do painel de equipe.
 *
 * A spec §9 (limite 2) oferecia duas saídas para «operador fora da janela do
 * painel»: aceitar o painel vazio, ou a seção listar só quem está na janela.
 * Nenhuma das duas serve — a primeira mostra vazio fingindo ser dado, a segunda
 * faz a busca mentir por omissão (o supervisor procura alguém que EXISTE e
 * recebe «não encontrado»).
 *
 * A saída é esta: `undefined` quando o operador não está na janela, e a ficha
 * mostra identidade de qualquer jeito, declarando que os números não estão
 * disponíveis. O repo já tem esse padrão — `AttendantVM.metricsAvailable`
 * existe justamente para «card degrada com placeholder honesto».
 *
 * Gate: `supervisor-dashboard-read`. Sem a cap o request NÃO sai (um atendente
 * chamando isto tomaria 403 a cada tecla).
 */
import { useCapability } from '../../hooks/use-capability';
import { useSupervisorDashboard } from '../supervisor/hooks/use-supervisor-dashboard';

import type { SupervisorPresenceStatus } from '../supervisor/types';

export interface OperatorLiveSignals {
  presence: SupervisorPresenceStatus;
  currentLoad: number;
  capacity: number;
  iaConducting?: number;
  behavioralPattern?: { label: string; severity: string } | null;
  lastActivityAt?: string | null;
}

export function useOperatorLiveSignals(userId: string | null): {
  data: OperatorLiveSignals | undefined;
} {
  const canRead = useCapability('supervisor-dashboard-read');
  const enabled = canRead && userId !== null && userId.length > 0;
  const { data } = useSupervisorDashboard(enabled);
  if (!enabled) return { data: undefined };

  const entry = data?.columnA.find((e) => e.userId === userId);
  if (entry === undefined) return { data: undefined };

  return {
    data: {
      presence: entry.presenceStatus,
      currentLoad: entry.currentLoad,
      capacity: entry.capacity,
      ...(entry.iaConducting !== undefined && { iaConducting: entry.iaConducting }),
      ...(entry.behavioralPattern !== undefined && {
        behavioralPattern: entry.behavioralPattern,
      }),
      ...(entry.lastActivityAt !== undefined && { lastActivityAt: entry.lastActivityAt }),
    },
  };
}
